import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import SortSelector from '@components/modal/SortSelector'
import IncidentCard from '@components/incidentCard/IncidentCard'

import SectionTitle from '@components/sectionTitle/SectionTitle'
import { RadioOption } from '@components/radio/Radio'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { isBefore } from 'date-fns'
import { isIncidentClosed, isIncidentOnStandBy } from '@utils/incidentUtils'
import { sortEnseigneIncidents } from '@screens/incident/utils'

interface Props {
  incidents: IncidentResponse[]
  showHotlineHomeVersion?: boolean
  onSeeAll?: () => void
}

interface ComponentProps {
  sections: IncidentSection[]
  reverse: boolean
  onOrderChange: (val: boolean) => void
  sort?: RadioOption
  onSortChange: (val?: RadioOption) => void
  sorts: RadioOption[]
  renderIncident: (incident: IncidentResponse) => React.ReactNode
}

interface IncidentSection {
  label: string
  incidents: IncidentResponse[]
  value: string
}

const IncidentListSectionWeb = ({
  sections,
  reverse,
  onOrderChange,
  sort,
  onSortChange,
  sorts,
  renderIncident,
}: ComponentProps) => {
  const i18n = useI18n()

  const renderSection = (section: IncidentSection) => (
    <SectionContainer key={section.label}>
      <SectionLabel>{`${section.label} (${section.incidents.length})`}</SectionLabel>
      {section.incidents.length > 0 ? (
        <IncidentRow>{section.incidents.map(renderIncident)}</IncidentRow>
      ) : (
        <MissingInfo>{i18n.t('components.incidentListSection.noIncident')}</MissingInfo>
      )}
    </SectionContainer>
  )

  return (
    <>
      <SortContainer>
        <SortSelector
          reverse={reverse}
          onOrderChange={onOrderChange}
          sort={sort}
          onSortChange={onSortChange}
          sorts={sorts}
        />
      </SortContainer>
      {sections.map(renderSection)}
    </>
  )
}

const IncidentListSectionMobile = ({
  sections,
  reverse,
  onOrderChange,
  sort,
  onSortChange,
  sorts,
  renderIncident,
}: ComponentProps) => {
  const i18n = useI18n()

  const tabs: { label: string; value: string }[] = sections.map(({ label, value, incidents }) => ({
    label: label + ` (${incidents.length})`,
    value,
  }))

  const [tab, setTab] = React.useState(tabs[0])

  const displayedIncidents = React.useMemo(() => {
    const section = sections.find((section) => section.value === tab.value)
    return section ? section.incidents : []
  }, [sections, tab])

  return (
    <>
      <SegmentedContainer>
        <SegmentedControl data={tabs} value={tab} onItemSelected={setTab} />
      </SegmentedContainer>

      <SortContainer>
        <SortSelector
          reverse={reverse}
          onOrderChange={onOrderChange}
          sort={sort}
          onSortChange={onSortChange}
          sorts={sorts}
        />
      </SortContainer>

      {displayedIncidents.length > 0 ? (
        displayedIncidents.map(renderIncident)
      ) : (
        <MissingInfo>{i18n.t('components.incidentListSection.noIncident')}</MissingInfo>
      )}
    </>
  )
}

const IncidentListSection = ({ incidents, onSeeAll, showHotlineHomeVersion }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const renderIncident = (incident: IncidentResponse) => (
    <IncidentContainer
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${incident.id}`, { incidentId: incident.id })}
      key={incident.id}>
      <IncidentCard cardHeight={cardHeight} setCardHeight={assignCardHeight} incident={incident} />
    </IncidentContainer>
  )

  const sorts: RadioOption[] = [
    { label: i18n.t('components.incidentListSection.sort.createdAt'), value: 'createdAt' },
    { label: i18n.t('components.incidentListSection.sort.modifiedAt'), value: 'modifiedAt' },
    { label: i18n.t('components.incidentListSection.sort.code'), value: 'code' },
    { label: i18n.t('components.incidentListSection.sort.enseigne'), value: 'enseigne' },
  ]

  const [sort, onSortChange] = React.useState<RadioOption | undefined>(sorts[0])
  const [reverse, onOrderChange] = React.useState(false)
  const [cardHeight, setCardHeight] = React.useState(0)

  const allIncidents = React.useMemo(() => {
    return incidents.filter((incident) => !isIncidentClosed(incident.status))
  }, [incidents])

  const tabs = showHotlineHomeVersion
    ? [
        {
          label: i18n.t('components.incidentListSection.waitingValidation'),
          value: 'waitingValidation',
          incidents: [],
        },
        { label: i18n.t('components.incidentListSection.refusedPresta'), value: 'refusedPresta', incidents: [] },
      ]
    : [
        { label: i18n.t('components.incidentListSection.standBy'), value: 'standBy', incidents: [] },
        { label: i18n.t('components.incidentListSection.inProgress'), value: 'inProgress', incidents: [] },
      ]

  const sections: IncidentSection[] = React.useMemo(() => {
    const orderedByStatus = allIncidents.reduce((acc, cur) => {
      acc[isIncidentOnStandBy(cur.status) ? 0 : 1].incidents.push(cur)
      return acc
    }, tabs as IncidentSection[])

    return orderedByStatus.map((section) => ({
      ...section,
      incidents:
        !!sort && sort.value === 'enseigne'
          ? sortEnseigneIncidents(section.incidents, reverse)
          : section.incidents.sort((a, b) => {
              const one = reverse ? -1 : 1
              const minusOne = reverse ? 1 : -1
              if (!!sort) {
                switch (sort.value) {
                  case 'modifiedAt' && !!a.datePriseEnCharge && b.datePriseEnCharge:
                    return isBefore(new Date(a.datePriseEnCharge!), new Date(b.datePriseEnCharge!)) ? one : minusOne

                  case 'code':
                    return one * a.numero.toString().localeCompare(b.numero.toString())

                  default:
                    return isBefore(new Date(a.dateDeCreation), new Date(b.dateDeCreation)) ? one : minusOne
                }
              }
              return 1
            }),
    }))
  }, [allIncidents, i18n, sort, reverse])

  const assignCardHeight = (height: number) => {
    if (height > cardHeight) {
      setCardHeight(height)
    }
  }

  return (
    <>
      {!!onSeeAll && (
        <SectionTitle
          title={i18n.t('components.incidentListSection.incidentTotal')}
          button={{
            label: i18n.t(`components.incidentListSection.${isPhone ? 'seeAll' : 'seeAllIncidents'}`),
            onClick: onSeeAll,
          }}
        />
      )}

      {isPhone ? (
        <IncidentListSectionMobile
          sections={sections}
          reverse={reverse}
          onOrderChange={onOrderChange}
          sort={sort}
          onSortChange={onSortChange}
          sorts={sorts}
          renderIncident={renderIncident}
        />
      ) : (
        <IncidentListSectionWeb
          sections={sections}
          reverse={reverse}
          onOrderChange={onOrderChange}
          sort={sort}
          onSortChange={onSortChange}
          sorts={sorts}
          renderIncident={renderIncident}
        />
      )}
    </>
  )
}

export default IncidentListSection

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
const SectionContainer = styled(View)`
  padding: 16px 0;
`
const SectionLabel = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 8px;
`
const IncidentRow = styled(View)`
  flex-flow: row wrap;
`
const IncidentContainer = styled(TouchableOpacity)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '4px 0px;' : '0px 16px 16px 0px')};
`
const SegmentedContainer = styled(View)`
  margin: 10px 0 16px;
`
const SortContainer = styled(View)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-bottom: 18px;' : 'margin: 24px 0px;')}
`
